import { useCallback, useEffect, useState } from "react";
import { Alert, Box, Button, Snackbar } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/app/providers";
import { registerWebPush } from "./registerWebPush";
import { getVapidPublicKey, isWebPushSupported } from "./vapid";

const DISMISS_KEY = "hagd_push_prompt_dismissed";

/**
 * Bandeau pour activer les notifications push (clic utilisateur requis par le navigateur).
 */
export function PushPermissionPrompt() {
  const { t } = useTranslation();
  const { token, currentBuilding } = useAuth();
  const buildingId = currentBuilding?.pk ?? null;
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token || buildingId == null) {
      setOpen(false);
      return;
    }
    if (!isWebPushSupported() || !getVapidPublicKey()) {
      setOpen(false);
      return;
    }
    if (Notification.permission !== "default") {
      setOpen(false);
      return;
    }
    if (sessionStorage.getItem(DISMISS_KEY) === "1") {
      setOpen(false);
      return;
    }
    setOpen(true);
  }, [token, buildingId]);

  const handleEnable = useCallback(async () => {
    if (!token || buildingId == null) return;
    setLoading(true);
    try {
      const result = await registerWebPush({
        token,
        buildingId,
        client: "accueilv2",
        requestPermission: true,
      });
      if (result.ok || Notification.permission !== "default") {
        setOpen(false);
      }
    } finally {
      setLoading(false);
    }
  }, [token, buildingId]);

  const handleDismiss = useCallback(() => {
    sessionStorage.setItem(DISMISS_KEY, "1");
    setOpen(false);
  }, []);

  return (
    <Snackbar
      open={open}
      autoHideDuration={null}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
    >
      <Alert
        severity="info"
        variant="filled"
        sx={{ alignItems: "center", width: "100%" }}
        action={
          <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
            <Button color="inherit" size="small" onClick={handleDismiss} disabled={loading}>
              {t("notifications.push.later", "Plus tard")}
            </Button>
            <Button color="inherit" size="small" variant="outlined" onClick={handleEnable} disabled={loading}>
              {t("notifications.push.enable", "Activer")}
            </Button>
          </Box>
        }
      >
        {t("notifications.push.prompt", "Activer les notifications pour être alerté en temps réel.")}
      </Alert>
    </Snackbar>
  );
}
